/**
 * A STREAM THAT NEVER ANSWERS MUST SAY SO.
 *
 * Cowork QA run 13 on beta, F1. Pressing Undo on a finalised batch
 * feeding produced an EMPTY assistant bubble that sat there for over
 * two minutes, survived a full iframe reload, and left both feedings
 * live; a second press went through in 71 ms. Beta's turn log has NO
 * ROW for the first press — the two that were logged ran in 95 and
 * 71 ms — so the request never produced a turn on the server at all:
 * the SSE connection stalled somewhere between the dock and the API,
 * and the panel had no way to notice. `isStreaming` stayed true, the
 * composer stayed disabled, and the only thing on screen was a spinner
 * in an empty bubble, which is indistinguishable from a turn that is
 * merely slow.
 *
 * So a streamed turn carries a WATCHDOG. Every chunk of any kind feeds
 * it; `done` clears it; silence for `STREAM_SILENCE_MS` fails the turn
 * through the same error branch a 503 goes through — the dock expands,
 * the collapsed row previews it, the composer is released, and the
 * bubble carries `NO_ANSWER_TEXT` rather than nothing.
 *
 * ⚠️ THESE TWO LIVE IN A MODULE OF THEIR OWN, on purpose. Three layers
 * read them — the panel, `aiApi`'s voice POST and the wire test — and
 * the two obvious homes are both booby-trapped: `services/streamingAPI`
 * is mocked wholesale by most of the froggerAI suites (a mock that
 * exports only `streamingAPI` would leave the voice timeout
 * `undefined`), and `aiApi` is mocked down to three functions by the
 * same suites. Nothing mocks this file.
 */

/**
 * How long a turn may go without a chunk of ANY kind before the panel
 * calls it dead. **120 seconds = EIGHT MISSED PINGS**, and it is
 * deliberately ABOVE the 90 s beta AI floor.
 *
 * ⚠️ THE SERVER KEEPS THE SOCKET WARM (review of 64044652, item 2).
 * Our chunks leave the graph only at NODE BOUNDARIES and the model
 * client retries with sleeps, so a perfectly live turn can genuinely
 * say nothing for minutes — a silence window alone would bury it. The
 * backend therefore emits `{"type": "ping"}` whenever a running turn
 * has produced nothing for **15 seconds**, and this window is eight of
 * those missed in a row: long enough that a hiccup in delivery is not a
 * burial, short enough that a keeper is not left reading a spinner.
 *
 * The emitter is `frogger_ai/langgraph/streaming.py` and ships in THIS
 * deploy. ⚠️ The client does not depend on it: a burial says the line
 * and hands the composer back but **leaves the socket open**, so a turn
 * buried for want of a ping still comes back the moment it speaks
 * (`FroggerAIChatPanel`'s `stall` / `revive`).
 *
 * The 90 s floor (`OPENAI_TIMEOUT_SECONDS`, `MCP_HTTP_TIMEOUT_SECONDS`
 * — see "Beta Timeout Policy — DO NOT TIGHTEN") governs the MODEL
 * CALLS inside a turn: how long the server may wait on Anthropic or on
 * a tool before it gives up. It says nothing about a socket that died
 * between the browser and the API, which is what this measures, and a
 * watchdog at or under the floor would abort turns the server is still
 * legitimately working on.
 *
 * It is a SILENCE timer, not a turn budget: any chunk — a delta, a
 * `tool_call`, a `counts` row, a `ping` — restarts it, so a turn that
 * is slow but alive runs as long as it needs to.
 */
export const STREAM_SILENCE_MS = 120000;

/**
 * What the failed turn SAYS. Verbatim, and the **"No answer arrived"**
 * prefix is the deploy marker for this fix — `grep` finds it in exactly
 * one built chunk.
 *
 * ⚠️ **IT MAY NEVER CLAIM NOTHING WAS WRITTEN** (review of 64044652,
 * item 1 — the BLOCKER, reproduced). The first cut said "nothing was
 * changed. Try again.", and that is a claim this side of the wire
 * cannot make. A turn stalls *after* its tool has run at least as often
 * as before: "I fed rack 1" streams a `tool_call` and a `tool_result`
 * — the feeding is COMMITTED by then — and it is the model call AFTER
 * it that hangs. Even with ZERO chunks delivered the claim is unsafe,
 * because the Undo press is settled server-side BEFORE the graph runs,
 * so the reversal can land with nothing ever reaching the browser. A
 * sentence that says "nothing changed" over a write that landed does
 * not merely mislead — it tells the keeper the safe move is to say it
 * again, which is the second feeding.
 *
 * So it reports what we KNOW (no answer came back) and hands the only
 * check that settles it (the page), and never guesses at the half we
 * cannot see.
 */
export const NO_ANSWER_TEXT = 'No answer arrived — the request may still '
  + 'have gone through. Check the page before repeating it.';

/**
 * What a turn says when its stream DROPPED AFTER SOMETHING ARRIVED
 * (review of 9ca56cb3, item 3). Appended under the text that did arrive,
 * never instead of it: a turn that streamed "Fed Rack 1 — 7 tanks" and its
 * Undo and then lost the socket has an answer on screen, and replacing it
 * with "No answer arrived" contradicts the receipt beneath it.
 * `NO_ANSWER_TEXT` is for a turn where nothing arrived at all.
 */
export const DROPPED_TEXT = 'The connection dropped before the answer finished '
  + '— check the page before repeating it.';
